import {
  RawVpcPeering,
  RawVpcPeeringRoute,
  RawVpcPeeringTunnel,
  VPC_PEERING_SSH_USER,
  VpcPeeringRouteStatus,
  VpcPeeringStatus
} from '@hevoio/hevo-ui-toolkit';
import {
  VPCPeeringConnection,
  VPCPeeringStatusValue
} from '../../containers/team/Connectivity/interface';
import { VPC_PEERING_SSH_PORT } from '../constants/vpcPeering';

/**
 * The connector helpers' one read of a saved config: `useSSH` is the only tunnel
 * flag in form state, and it means "some tunnel is configured" — peering or the
 * customer's own bastion alike. Which of the two it is gets derived at render
 * time by the toolkit's `isPeeringShaped`, from `ssh_user`.
 */
export function sshFormStateFromRawConfig(rawData: {
  ssh_host?: unknown;
  ssh_port?: unknown;
  ssh_user?: unknown;
}): { useSSH: boolean } {
  return { useSSH: !!(rawData?.ssh_host || rawData?.ssh_port || rawData?.ssh_user) };
}

/**
 * Standard's peering spec, collapsed client-side from the team setting plus the
 * customer-facing peering list — `vpc_peering_host` for the tunnel host and
 * `GET /connectivity-service/api/peering/requests` for the routes and their
 * acceptance state (INF-1743, "Backend contract — Standard").
 *
 * Duplicated knowingly: the endpoint that would have made it one implementation
 * (`GET /config/v2.0/connectivity/vpc-peering`) is not available, so the ticket's
 * settled decisions 2 and 3 no longer hold for Standard. If it ever ships, this
 * function is what gets deleted.
 *
 * The canonical implementation is `_spec_tunnel` / `peering_spec` in
 * connectivity-service `app/api/internal.py` — Python, and still unmerged
 * (connectivity-service PR #31). It is NOT hermes's `PeeringSpecMapper`: no such
 * class exists in hermes `dev` or in PR #1396, and earlier revisions of this
 * comment, of R15 and of the ticket named one. hermes's `ConnectivityHttpRouter`
 * deserialises that endpoint's response straight into `VpcPeeringSpec`.
 *
 * Edge is unaffected by this file, but is not live either: hermes #1396 is open
 * and the endpoint it calls is open, so Edge renders no `vpc_peering` today.
 *
 * The collapse rule, mirroring the Python one:
 *  - Only ACTIVE and PENDING_ACCEPTANCE rows count. PENDING, REQUEST_SUBMITTED,
 *    FAILED and DELETED are not routable and are not "claimed but not yet
 *    routed" either, so they are dropped rather than greyed.
 *  - Rows group into tunnels by host: `hevo_tunnel_private_ip` when the row
 *    carries one, otherwise the team-setting host. One host per intermediate
 *    VPC per region is the backend's own invariant.
 *  - A tunnel is AVAILABLE once any of its routes is ACTIVE; otherwise it is
 *    PENDING_ACCEPTANCE, which the form shows disabled rather than hiding
 *    (ticket AC).
 *  - Each row contributes one route per CIDR, carrying its own status, so a
 *    claimed-but-unrouted range greys instead of disappearing.
 *
 * `port` and `user` are constants here, not payload: `/peering/requests` is the
 * customer-facing endpoint and does not serve the SSH tunnel triple (ticket
 * comment, connectivity-service #27). Only the internal `/internal/v1/peerings`
 * does, and hermes is its only consumer.
 */
export function vpcPeeringFromConnections(
  connections: VPCPeeringConnection[] | undefined,
  teamHost: string | null | undefined
): RawVpcPeering | undefined {
  const offered = (connections ?? []).filter(
    connection =>
      connection?.status?.value === VPCPeeringStatusValue.ACTIVE ||
      connection?.status?.value === VPCPeeringStatusValue.PENDING_ACCEPTANCE
  );

  // No usable row, but the team setting says this deployment has a tunnel:
  // offer it with no routes rather than withdrawing the toggle. The setting is
  // what force-injected the tunnel before this ticket, so trusting it is the
  // behaviour-preserving side of an unreachable or empty connectivity-service
  // (ticket AC: a fetch failure must never surface as a form error).
  if (!offered.length) {
    return teamHost ? singleTunnelSpec(teamHost) : undefined;
  }

  const byHost = new Map<string, RawVpcPeeringTunnel>();

  offered.forEach(connection => {
    const host = connection.hevo_tunnel_private_ip || teamHost;

    // A row with no host on either side names no tunnel to connect through.
    if (!host) {
      return;
    }

    const routeStatus =
      connection.status.value === VPCPeeringStatusValue.ACTIVE
        ? VpcPeeringRouteStatus.ACTIVE
        : VpcPeeringRouteStatus.PENDING_ACCEPTANCE;

    const routes: RawVpcPeeringRoute[] = (connection.cidr ?? []).map(cidr => ({
      cidr,
      name: connection.name,
      aws_region: connection.aws_region,
      status: routeStatus
    }));

    const tunnel = byHost.get(host) ?? {
      host,
      // The customer-facing row carries the CUSTOMER's vpc_id, not Hevo's
      // intermediate VPC, so there is nothing truthful to put here. It only
      // labels the multi-tunnel picker, and an empty label is better than a
      // wrong one.
      intermediate_vpc: '',
      status: VpcPeeringStatus.PENDING_ACCEPTANCE,
      routes: []
    };

    tunnel.routes.push(...routes);

    if (routeStatus === VpcPeeringRouteStatus.ACTIVE) {
      tunnel.status = VpcPeeringStatus.AVAILABLE;
    }

    byHost.set(host, tunnel);
  });

  const tunnels = [...byHost.values()];

  if (!tunnels.length) {
    return teamHost ? singleTunnelSpec(teamHost) : undefined;
  }

  // An AVAILABLE tunnel has to come first. The toolkit's `resolveTunnel` falls
  // back to `tunnels[0]` regardless of status while the spec reads AVAILABLE
  // (it is AVAILABLE if ANY tunnel is), so a PENDING_ACCEPTANCE tunnel sitting
  // in first position becomes the create default — the form fills a tunnel the
  // panel itself badges "Awaiting acceptance", and Test Connection fails as an
  // opaque SSH timeout. Sort is stable, so API row order survives within a
  // status group.
  tunnels.sort(
    (a, b) =>
      (a.status === VpcPeeringStatus.AVAILABLE ? 0 : 1) -
      (b.status === VpcPeeringStatus.AVAILABLE ? 0 : 1)
  );

  // Keep the team-setting host addressable when no row names it.
  //
  // Saved peered configs carry whatever `ssh.service` force-injected before
  // this ticket, which is the team setting. The two sources diverge — the
  // setting is a snapshot written only on a transition into ACTIVE, the rows
  // are a live join — so for a pre-service team the saved `ssh_host` appears in
  // no row. Without it in the list the toolkit cannot resolve the saved host,
  // falls back to `tunnels[0]`, and its self-heal effect rewrites `ssh_host` on
  // OPEN of an edit form, marks the form dirty and claims the tunnel moved. The
  // next save persists a host whose routability nobody has confirmed.
  //
  // Appended, never prepended: a new config still defaults to a row tunnel,
  // which is the better-sourced value. This only keeps an existing one
  // resolvable.
  if (teamHost && !tunnels.some(tunnel => tunnel.host === teamHost)) {
    tunnels.push({
      host: teamHost,
      intermediate_vpc: '',
      status: VpcPeeringStatus.AVAILABLE,
      routes: []
    });
  }

  return {
    port: VPC_PEERING_SSH_PORT,
    user: VPC_PEERING_SSH_USER,
    status: tunnels.some(tunnel => tunnel.status === VpcPeeringStatus.AVAILABLE)
      ? VpcPeeringStatus.AVAILABLE
      : VpcPeeringStatus.PENDING_ACCEPTANCE,
    tunnels
  };
}

/** The team setting on its own: one usable tunnel, no routes to render. */
function singleTunnelSpec(host: string): RawVpcPeering {
  return {
    port: VPC_PEERING_SSH_PORT,
    user: VPC_PEERING_SSH_USER,
    status: VpcPeeringStatus.AVAILABLE,
    tunnels: [
      {
        host,
        intermediate_vpc: '',
        status: VpcPeeringStatus.AVAILABLE,
        routes: []
      }
    ]
  };
}
